import { FC, ReactNode, useEffect } from 'react';

import {
  ContentLayoutVariant,
  HasCommonContent,
} from '@silvertours/common-landingpages-view';
import { Runtime, analytics } from '@silvertours/front-entities';
import {
  PageLegacy,
  PageNavigationLegacy,
  Suppliers,
  MarketingSlider,
  AppInstallBanner,
  useShowAppBanner,
  ErrorPage,
} from '@silvertours/front-features';
import { ContentModulesRenderer } from './content';
import { ContentModules } from './ContentModules';
import { resolveVariantConfig } from './Body.variant';

type BodyProps = HasCommonContent & {
  variant: ContentLayoutVariant;
  stage?: ReactNode;
  title?: string;
  content: ContentModulesRenderer;
  introductionContent?: ReactNode;
  author?: ReactNode;
  statusCode?: number;
};

const Body: FC<BodyProps> = ({
  variant,
  title,
  breadcrumbs,
  content,
  suppliers,
  sliderCards,
  stage,
  author,
  introductionContent,
  statusCode,
}) => {
  const { language } = Runtime.useLocale();
  const { isShowing, onClose } = useShowAppBanner(language);

  const {
    includeSearch,
    includeTrustSeals,
    isMinimal,
    isPlain,
    isPromo,
    isErrorPage,
    showMarketingSlider,
    showBreadcrumbs,
  } = resolveVariantConfig(variant);

  useEffect(() => {
    if (isShowing) {
      analytics.gtm.trackAppPromoBannerViewed();
    }
  }, [isShowing]);

  return (
    <PageLegacy.PageContentBase>
      {isPromo && introductionContent}
      {isErrorPage && (
        <ErrorPage.Intro statusCode={statusCode as ErrorPage.ErrorCode} />
      )}
      {stage}
      {!isErrorPage && (
        <Suppliers.Suppliers
          title={title}
          suppliers={suppliers.content}
          includeSearch={includeSearch}
          includeTrustSeals={includeTrustSeals}
          isMinimal={isMinimal}
          isPlain={isPlain}
          author={author}
        />
      )}
      {showMarketingSlider && (
        <MarketingSlider.MarketingSlider
          sliderCards={sliderCards?.content?.sliderCards}
        />
      )}
      {isErrorPage && <ErrorPage.InfoCards />}
      {breadcrumbs && showBreadcrumbs && (
        <PageNavigationLegacy.Breadcrumbs breadcrumbs={breadcrumbs.links} />
      )}
      <ContentModules content={content} />
      <>
        <div id="fglayer" role="application" />
        <AppInstallBanner isShowing={isShowing} onClose={onClose} />
      </>
    </PageLegacy.PageContentBase>
  );
};

export { Body };
